import notificationService from './notificationService';

const PushNotificationService = (requestFn = null) => {
  const serviceState = {
    registration: null,
    subscription: null,
    publicKey: process.env.REACT_APP_VAPID_PUBLIC_KEY
  };


  // Register service worker
  const registerServiceWorker = async () => {
    try {
      if (!('serviceWorker' in navigator)) {
        throw new Error('Service Worker not supported in this browser');
      }

      const publicUrl = process.env.PUBLIC_URL || '';
      const swPath = `${publicUrl}/sw.js`;
      serviceState.registration = await navigator.serviceWorker.register(swPath);
      await navigator.serviceWorker.ready;

      return serviceState.registration;
    } catch (error) {
      throw error;
    }
  }

  // Initialize service worker and request permission
  const initialize = async (userId) => {
    try {
      // Register service worker
      if ('serviceWorker' in navigator) {
        const publicUrl = process.env.PUBLIC_URL || '';
        const swPath = `${publicUrl}/sw.js`;
        serviceState.registration = await navigator.serviceWorker.register(swPath);
      }

      // Request notification permission
      const permission = await this.requestPermission();
      if (permission === 'granted') {
        await this.subscribeToPushNotifications(userId);
      }

      return permission;
    } catch (error) {
      console.error('Push notification initialization failed:', error);
      throw error;
    }
  }

  // Request notification permission
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      throw new Error('This browser does not support notifications');
    }

    let permission = Notification.permission;

    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    return permission;
  }

  // Subscribe to push notifications
  const subscribeToPushNotifications = async (userId) => {
    try {
      if (!serviceState.registration) {
        throw new Error('Service Worker not registered');
      }

      // Check if already subscribed
      serviceState.subscription = await serviceState.registration.pushManager.getSubscription();

      if (!serviceState.subscription) {
        // Create new subscription
        serviceState.subscription = await serviceState.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(this.publicKey)
        });
      }

      // Send subscription to backend
      await sendSubscriptionToServer(userId, serviceState.subscription);

      return serviceState.subscription;
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  // Send subscription to backend
  const sendSubscriptionToServer = async (userId, subscription) => {
    const payload = {
      userId,
      endpoint: subscription.endpoint,
      p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
      auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
    };
    if (requestFn) {
      return await requestFn('POST', '/notifications/subscribe', payload);
    } else {
      console.warn('No request function provided to pushNotificationService');
      return null;
    }
    // return await request('POST', '/notifications/subscribe', payload);
  }

  // Unsubscribe from push notifications
  const unsubscribe = async (userId) => {
    try {
      if (serviceState.subscription) {
        await serviceState.subscription.unsubscribe();

        // Notify backend
        const { request } = notificationService();
        await request('POST', '/notifications/unsubscribe', { userId });

        serviceState.subscription = null;
      }
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      throw error;
    }
  }

  // Check if notifications are supported and enabled
  const isSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  const getPermissionStatus = () => {
    return Notification.permission;
  }

  // Utility function to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  return {
    registerServiceWorker,
    initialize,
    requestPermission,
    subscribeToPushNotifications,
    unsubscribe,
    isSupported,
    getPermissionStatus,
    get registration() { return serviceState.registration; },
    get subscription() { return serviceState.subscription; }
  };
};


export default PushNotificationService;